import { TPopupInitialState, TPopupList } from '@@types/popup';
import Alert from '@components/popups/alert';
import Confirm from '@components/popups/confirm';
import Drawer from '@components/popups/drawer';
import PopupModal from '@components/popups/modal';
import usePopup, { POPUP_CONTENTS, TPopupComponentKeys } from '@hooks/usePopup';
import { RootState } from '@stores/store';
import { useRouter } from 'next/router';
import { ComponentType, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';

const POPUP_COMPONENTS = {
  confirm: Confirm,
  modal: PopupModal,
  drawer: Drawer,
  promiseConfirm: Confirm,
  promiseModal: PopupModal,
  promiseDrawer: Drawer,
};

export default function PopupContainer() {
  const popupList = useSelector((state: RootState) => state.popup);
  const { openPopup, closePopup, closePopupByQueryId, openPromisePopup } =
    usePopup();
  const router = useRouter();

  const renderPopup = popupList.map(
    (
      { type, props, isOpen, Component, popupOptions, alertOptions, queryId },
      index,
    ) => {
      if (type === 'alert') {
        if (!alertOptions?.resolve) {
          return;
        }
        return (
          <Alert key={index} {...alertOptions} closePopup={closePopup}></Alert>
        );
      }

      if (!type || !props || !isOpen) return;
      let _closePopup = () => {
        closePopup(queryId);
      };
      let _props = props;
      if (type.includes('promise')) {
        const _resolve = props.resolve ?? Promise.resolve;

        _closePopup = () => {
          closePopup(queryId);
          _resolve(false);
        };
        _props = {
          ...props,
          resolve: (value: unknown) => {
            _resolve(value);
            closePopup(queryId);
          },
        };
      }
      const PopupComponent = POPUP_COMPONENTS[type];
      if (!Component) {
        _closePopup();
        return <></>;
      }
      return (
        <PopupComponent
          key={index}
          isOpen={isOpen}
          closePopup={_closePopup}
          props={_props}
          popupOptions={popupOptions as any}
        >
          <Suspense>
            <Component props={_props} closePopup={_closePopup} />
          </Suspense>
        </PopupComponent>
      );
    },
  );

  const initPopup = () => {
    const query = router.query;
    let pids = query.pid;
    if (!pids) return;

    if (!Array.isArray(pids)) {
      pids = [pids];
    }
    pids.forEach((pid) => {
      const splited = pid.split('@');
      let [queryId, popupInfo] = splited as [
        TPopupComponentKeys,
        TPopupInitialState<unknown> | undefined,
      ];

      if (!popupInfo) return;

      popupInfo = JSON.parse(popupInfo as string);

      const comp = POPUP_CONTENTS[queryId];

      if (!comp) return;

      const isExist = popupList.some((list) => list?.queryId === queryId);
      if (!isExist) {
        openPopup({
          isOpen: true,
          Component: comp as
            | ComponentType<{ props: any; closePopup: any }>
            | ComponentType<{ props: any; closePopup: any }>
            | undefined,
          queryId,
          props: popupInfo?.props || {},
          popupOptions: popupInfo?.popupOptions,
          type: (popupInfo?.type as TPopupList) || 'modal',
        });
      }
    });
  };

  const changeRouterEvent = () => {
    const queries = router.query;
    const pids = queries.pid;
    const hasQueryId = popupList
      .filter((list) => list.queryId)
      .map((list) => list.queryId);

    if (hasQueryId.length === 0) return;

    if (!pids) {
      closePopupByQueryId([...(hasQueryId as TPopupComponentKeys[])]);
    }

    const pidsArray = Array.isArray(pids) ? pids : [pids];

    const pidIds = pidsArray.map((_pid) => _pid?.split('@')[0]);

    const willRemoveIds = hasQueryId.filter((_id) => !pidIds.includes(_id));

    if (willRemoveIds.length === 0) return;
    closePopupByQueryId(willRemoveIds as TPopupComponentKeys[]);
  };

  useEffect(() => {
    initPopup();
  }, []);

  useEffect(() => {
    changeRouterEvent();
  }, [router]);

  return <>{renderPopup}</>;
}
